import { ExportButton } from 'components/Shared/MuiTable/ActionButton'
import Checkbox from 'particles/Checkbox'
import Search from 'particles/Search'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectRequestsAreLoading, selectSubmissionsList } from 'state'
import {
    MAX_PAGE_SIZE,
    REVIEW_MODAL_TYPE,
    resetMultiSelectSetting,
    selectActivePageNum,
    selectCheckCanReviewSubmissionByStep,
    selectIsMassModalBlocked,
    selectMassSubmissionUuids,
    selectReviewPendingSubmission,
    selectSelectedCampaignSummary,
    selectSelectedCampaignUuid,
    selectSelectedMuiTableRef,
    selectSelectedRegistrationView,
    selectSelectedSubmissionView,
    selectSidebarStep,
    selectSidebarTask,
    selectSubmissionListParams,
    setMassSubmissionUuids,
    setSearchTerm,
} from 'state/review'
import { SUBMISSION_VIEW_ENUM, SubmissionState, exportSubmissions, massSubmission } from 'state/submissions'
import MassModalWindow from '../../common/MassModalWindow'
import { GLOBAL_MODAL_TYPE, closeModal, openModal, openModalWithProps } from 'state/constants'
import { isShowCardReviewComponent } from 'components/utils'
import { useTranslation } from 'react-i18next'
import ExportModal from '../../common/ExportModal'
import { STATE_KEYS } from 'state/requests'
import { RequestExportSubmissionsPayload } from 'services/api/submissions'
import { isAllowedReviewStatus, isNoneSubmitBlockExisted } from 'components/utils/submission'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { SubmissionFilterChip, SubmissionFilter } from './Filter'
import { ToolbarContainer } from './style'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'
import Text from 'particles/Text'
import { NewTrans } from 'hooks'
import { HandleMassSubmissionPayload } from '../../common/types'
import Menu from 'particles/Menu'
import RectangleButton from 'particles/Button/RectangleButton'
import MenuItem from 'particles/MenuItem'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'

interface ToolbarProps {
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
}

const Toolbar = ({ handleEditTagChange, handleRejectTagChange }: ToolbarProps) => {
    const dispatch = useDispatch()
    const [t] = useTranslation()
    const selectedMuiApiRef = useSelector(selectSelectedMuiTableRef)
    const submissions = useSelector(selectSubmissionsList)
    const massSubmissionUuids = useSelector(selectMassSubmissionUuids)
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const isMassModalBlocked = useSelector(selectIsMassModalBlocked)
    const selectedStep = useSelector(selectSidebarStep)
    const selectedTask = useSelector(selectSidebarTask)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedRegistrationView = useSelector(selectSelectedRegistrationView)
    const isVerifyMode = useSelector(selectReviewPendingSubmission)
    const isSubmissionExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportSubmissions]))
    const activePageNum = useSelector(selectActivePageNum)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const checkCanReviewSubmission = useSelector(selectCheckCanReviewSubmissionByStep)
    const [isOpenMoreActions, setOpenMoreActions] = useState(false)
    const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<null | HTMLElement>(null)
    const isCampaignEnded = selectedCampaignSummary?.status === CAMPAIGN_STATUS_ENUM.CLOSED

    const [isMassSelect, setIsMassSelect] = useState(false)
    const offset: number = activePageNum * MAX_PAGE_SIZE

    const showCardSubmissions = useMemo(() => {
        if (selectedStep) {
            if (selectedStep.type === STEP_TYPE_ENUM.CUSTOM) {
                if (isNoneSubmitBlockExisted(selectedStep)) {
                    return false
                }

                return true
            }
            if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                return true
            }
            return isShowCardReviewComponent(selectedStep.type)
        }
        return false
    }, [selectedStep, selectedSubmissionView])

    // hide button when campaign close or in verify mode
    const showReviewButtons = selectedCampaignSummary?.status !== CAMPAIGN_STATUS_ENUM.CLOSED && !isVerifyMode
    const showSelectAllButton =
        selectedStep &&
        checkCanReviewSubmission(selectedStep.type) &&
        !isCampaignEnded &&
        isAllowedReviewStatus(selectedSubmissionView) &&
        submissions.length > 0 &&
        !isVerifyMode

    useEffect(() => {
        if (massSubmissionUuids.length === submissions.length) {
            setIsMassSelect(true)
        } else {
            setIsMassSelect(false)
        }
    }, [massSubmissionUuids.length, submissions.length])

    const handleOpenMoreActions = (event: React.MouseEvent<HTMLElement>) => {
        setMoreActionsAnchorEl(event.currentTarget)
        setOpenMoreActions(true)
    }
    const handleCloseMoreActions = () => {
        setMoreActionsAnchorEl(null)
        setOpenMoreActions(false)
    }

    const handleCheck = () => {
        const newValue = !isMassSelect
        setIsMassSelect(newValue)

        if (newValue) {
            const submissionUuids = submissions
                .map((submission: SubmissionState) => submission.uuid)
                .slice(0, MAX_PAGE_SIZE)
            dispatch(setMassSubmissionUuids(submissionUuids))
            if (selectedMuiApiRef && selectedMuiApiRef.current) {
                const allRowIds = submissions.map((submission: SubmissionState) => submission.uuid)
                selectedMuiApiRef.current.selectRows(allRowIds, true)
            }
        } else {
            dispatch(resetMultiSelectSetting())
        }
    }

    const handleMassSubmission =
        (action_name: string) =>
        (payload: HandleMassSubmissionPayload): void => {
            const { comment = '', tag_uuids, post_time_window } = payload || {}
            if (selectedCampaignUuid) {
                dispatch(
                    massSubmission({
                        campaignUuid: selectedCampaignUuid,
                        uuids: massSubmissionUuids,
                        action_name,
                        comment,
                        tag_uuids,
                        post_time_window,
                        getSubmissionListPayload: {
                            offset,
                            stepUuid: selectedStep?.uuid,
                            ...submissionListParams,
                        },
                    }),
                )
            }
        }

    const handleOnOpenModal = (type: GLOBAL_MODAL_TYPE) => (): void => {
        dispatch(openModal(type))
    }

    const setSearch = (keywords: string) => {
        dispatch(setSearchTerm(keywords))
    }

    const onClearSearchTerm = () => {
        setSearch('')
    }

    const handleClickExport = () => {
        if (!selectedCampaignUuid || !selectedStep || !selectedSubmissionView) return
        return dispatch(
            exportSubmissions({
                uuid: selectedCampaignUuid,
                viewType: selectedSubmissionView as RequestExportSubmissionsPayload['viewType'],
                stepUuid: selectedStep.uuid,
            }),
        )
    }

    if (
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.REJECTED ||
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.DROPOUTS ||
        !showCardSubmissions ||
        selectedRegistrationView !== null ||
        !selectedStep ||
        !selectedTask
    )
        return null

    const enableExport = selectedSubmissionView !== SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS && !isVerifyMode

    const status = selectedSubmissionView
    const stepName = selectedStep.data.title
    const taskName = selectedTask.title

    const modalDescription = (
        <NewTrans i18nKey="modalWindow.exportSubmissionsTableSubtitle">
            <Text scale="bodyM400">
                You’re requesting to export the list of {{ status }} submissions for {{ taskName }} - {{ stepName }} for
                this campaign.
            </Text>
            <Text scale="bodyM400">
                It might take a few minutes for the file to be generated. Once this process is complete, the exported
                file will be sent to the email address associated with your current CM account.
            </Text>
        </NewTrans>
    )

    const getMassActions = () => {
        if (!showReviewButtons || !isAllowedReviewStatus(selectedSubmissionView)) {
            return null
        }
        if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW) {
            return (
                <>
                    <MassModalWindow
                        type={REVIEW_MODAL_TYPE.APPROVE_PENDING}
                        selectedNum={massSubmissionUuids.length}
                        handleMassApprovePending={handleMassSubmission('approve_pending_action')}
                    />
                    <MassModalWindow
                        type={REVIEW_MODAL_TYPE.REJECT_PENDING}
                        selectedNum={massSubmissionUuids.length}
                        handleMassRejectPending={handleMassSubmission('reject_pending_action')}
                    />
                </>
            )
        }

        return (
            <>
                {isMassModalBlocked ? (
                    <MassModalWindow type={REVIEW_MODAL_TYPE.BLOCK_BY_DETAIL_ACTION} />
                ) : (
                    <MassModalWindow
                        type={REVIEW_MODAL_TYPE.APPROVE}
                        selectedNum={massSubmissionUuids.length}
                        handleMassApprove={handleMassSubmission('approve')}
                    />
                )}
                <MassModalWindow
                    type={REVIEW_MODAL_TYPE.EDIT_REQUEST}
                    selectedNum={massSubmissionUuids.length}
                    handleMassEditRequest={handleMassSubmission('request_edit')}
                    handleEditTagChange={handleEditTagChange}
                />
                <MassModalWindow
                    type={REVIEW_MODAL_TYPE.REJECT}
                    selectedNum={massSubmissionUuids.length}
                    handleMassReject={handleMassSubmission('reject')}
                    handleRejectTagChange={handleRejectTagChange}
                />
            </>
        )
    }

    const getRightActionToolbar = () => {
        if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW) {
            return (
                <>
                    <RectangleButton
                        form="text"
                        size="s"
                        color="neutral"
                        label={t('button.moreActions')}
                        icon="dotsThreeOutline"
                        iconForm="fill"
                        onClick={handleOpenMoreActions}
                    />
                    <Menu
                        size="s"
                        open={isOpenMoreActions}
                        anchorEl={moreActionsAnchorEl}
                        onClose={handleCloseMoreActions}
                        width={200}
                    >
                        <MenuItem.Text
                            icon="export"
                            text={t('button.export')}
                            onClick={() => {
                                handleOnOpenModal(GLOBAL_MODAL_TYPE.REQUEST_EXPORT)()
                                handleCloseMoreActions()
                            }}
                            isDisabled={!!massSubmissionUuids.length || submissions.length === 0}
                        />
                        <MenuItem.Text
                            text={t('button.viewSubmissionRequirements')}
                            icon="fileText"
                            onClick={() => {
                                dispatch(closeModal())
                                // bypass batch dispatch, reopen modal to make it center
                                setTimeout(() => {
                                    dispatch(
                                        openModalWithProps({
                                            type: GLOBAL_MODAL_TYPE.BRIEF_REQUIREMENT,
                                            // task & step from sidebar, since toolbar review only exist in single task view
                                            task: selectedTask,
                                            stepId: selectedStep.uuid,
                                        }),
                                    )
                                }, 0)
                                handleCloseMoreActions()
                            }}
                        />
                    </Menu>
                </>
            )
        }

        if (enableExport) {
            return (
                <ExportButton
                    onClick={handleOnOpenModal(GLOBAL_MODAL_TYPE.REQUEST_EXPORT)}
                    isDisable={!!massSubmissionUuids.length || submissions.length === 0}
                />
            )
        }

        return null
    }

    return (
        <ToolbarContainer>
            <div className="main-toolbar">
                <div className="left-tools">
                    {getMassActions()}

                    <SubmissionFilter />
                </div>
                <div className="right-tools">
                    <Search
                        width="300px"
                        placeholder={t('placeholderText.enterNameUsername')}
                        size="xs"
                        onSearch={setSearch}
                        onClearSearch={onClearSearchTerm}
                        collapsible
                    />

                    {getRightActionToolbar()}
                </div>
            </div>

            <SubmissionFilterChip />

            {showSelectAllButton && (
                <Checkbox
                    size="s"
                    label={t('button.selectAll')}
                    onChange={handleCheck}
                    checkbox={isMassSelect ? 'selected' : 'notSelected'}
                />
            )}

            <ExportModal
                tableTitle={selectedStep.data.title}
                isRequesting={isSubmissionExportRequesting}
                onClickExport={handleClickExport}
                DescriptionComponent={modalDescription}
            />
        </ToolbarContainer>
    )
}

export default Toolbar
